import { useId, useState, type ReactNode } from 'react';
import { ACCOUNTING_GLOSSARY } from './statusCopy';

/** Tooltip discreto para conceptos contables (hover + focus). */
export function AccountingTooltip({
  term,
  children,
  text,
}: {
  term?: keyof typeof ACCOUNTING_GLOSSARY | string;
  text?: string;
  children: ReactNode;
}) {
  const id = useId();
  const tip = text ?? (term ? ACCOUNTING_GLOSSARY[term] : undefined);
  if (!tip) return <>{children}</>;

  return (
    <span className="relative inline-flex items-center group">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 rounded"
        aria-describedby={id}
      >
        {children}
        <span className="text-[10px] text-slate-400 border border-slate-300 rounded-full w-3.5 h-3.5 inline-flex items-center justify-center leading-none" aria-hidden>
          ?
        </span>
      </button>
      <span
        id={id}
        role="tooltip"
        className="pointer-events-none absolute left-0 top-full z-30 mt-1 hidden w-56 rounded-md border bg-white px-2.5 py-2 text-xs text-gray-700 shadow-md group-hover:block group-focus-within:block"
      >
        {tip}
      </span>
    </span>
  );
}

type ConfirmKind = 'post' | 'close' | 'reopen' | 'reverse' | 'deactivate';

const KIND_COPY: Record<
  ConfirmKind,
  { title: string; confirmLabel: string; tone: string }
> = {
  post: {
    title: 'Contabilizar',
    confirmLabel: 'Contabilizar',
    tone: 'bg-emerald-700 hover:bg-emerald-800',
  },
  close: {
    title: 'Cerrar período',
    confirmLabel: 'Cerrar período',
    tone: 'bg-slate-900 hover:bg-slate-800',
  },
  reopen: {
    title: 'Reabrir período',
    confirmLabel: 'Reabrir',
    tone: 'bg-amber-600 hover:bg-amber-700',
  },
  reverse: {
    title: 'Revertir',
    confirmLabel: 'Confirmar reversión',
    tone: 'bg-rose-700 hover:bg-rose-800',
  },
  deactivate: {
    title: 'Desactivar',
    confirmLabel: 'Desactivar',
    tone: 'bg-rose-700 hover:bg-rose-800',
  },
};

/**
 * Confirmación específica con consecuencias visibles.
 * Reemplaza window.confirm / modales ad-hoc.
 */
export function AccountingConfirmDialog({
  open,
  kind,
  title,
  consequences,
  reasonRequired,
  defaultReason = '',
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  kind: ConfirmKind;
  title?: string;
  consequences: string[];
  reasonRequired?: boolean;
  defaultReason?: string;
  busy?: boolean;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState(defaultReason);
  const copy = KIND_COPY[kind];
  const titleId = useId();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Cerrar diálogo"
        onClick={onCancel}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative z-10 w-full max-w-md rounded-lg border bg-white p-5 shadow-xl space-y-3"
      >
        <h3 id={titleId} className="text-lg font-semibold text-gray-900">
          {title ?? copy.title}
        </h3>
        <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
          {consequences.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        {reasonRequired && (
          <label className="block text-sm">
            <span className="text-xs text-gray-500">Motivo</span>
            <input
              className="mt-1 w-full border rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
            />
          </label>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-3 py-2 text-sm border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-slate-400"
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={busy || (reasonRequired && !reason.trim())}
            className={`px-3 py-2 text-sm rounded-md text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-offset-1 ${copy.tone}`}
            onClick={() => onConfirm(reasonRequired ? reason : undefined)}
          >
            {copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Tabla con scroll horizontal controlado y tipografía tabular en importes. */
export function ScrollTable({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border bg-white overflow-hidden ${className}`}>
      <div className="overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]">
        {children}
      </div>
    </div>
  );
}

/** Clase para celdas de importe: no se cortan, alineadas. */
export const moneyCellClass =
  'px-3 py-2 text-right tabular-nums whitespace-nowrap font-medium';

/** Columna secundaria: oculta en &lt; md. */
export const secondaryColClass = 'hidden md:table-cell';
